/**
 * Defense Technology Intelligence — analyst workspace template.
 *
 * Fuses the Publications/Research API and the PLA Contracts/Procurement API
 * into a single interactive intelligence graph:
 *
 *   Organizations → Research → Technology → PLA → Procurement → Suppliers
 *
 * Orchestration:
 * - Search runs BOTH APIs simultaneously (services/intelligenceGraph).
 * - Cluster-first rendering: the root entity is surrounded by category
 *   clusters with counts; clicking a cluster reveals its highest-value
 *   nodes rather than immediately rendering hundreds of individual nodes.
 * - Node selection opens the right-side intelligence/evidence panel; node
 *   expansion re-queries the appropriate API(s) and animates new nodes
 *   outward from the expanded node.
 * - Every edge preserves API provenance so the analyst can always move from
 *   a relationship back to its underlying evidence.
 */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  IntelCluster,
  IntelClusterKey,
  IntelEdge,
  IntelNode,
  IntelligenceGraphSnapshot,
} from '../../../types/intelligenceGraph';
import {
  IntelligenceGraphBuilder,
  buildClusters,
  expandNode as expandNodeService,
  expandOrganization,
  isExpandableNode,
  logGraphIntegrityWarnings,
  makeNodeId,
  validateGraphIntegrity,
} from '../../../services/intelligenceGraph';
import {
  ContextMenuActions,
  IntelligenceGraph,
  RenderLink,
  RenderNode,
} from '../../intelligence-graph/IntelligenceGraph';
import {
  IntelligenceGraphToolbar,
  ResultSize,
} from '../../intelligence-graph/IntelligenceGraphToolbar';
import { GraphClusterBar } from '../../intelligence-graph/GraphCluster';
import { EntityDetailsPanel } from '../../intelligence-graph/EntityDetailsPanel';
import { GraphLegend } from '../../intelligence-graph/GraphLegend';
import { useWindowSize } from '../../../hooks/useWindowSize';

/** Initial development/test entity — CETC 14th Research Institute. */
export const DEFAULT_SEARCH_ENTITY = '中国电子科技集团公司第十四研究所';

/** How many top members a cluster reveals per expansion click. */
const CLUSTER_EXPANSION_BATCH = 12;
/** How many new neighbors a node expansion reveals. */
const NODE_EXPANSION_REVEAL = 15;

/**
 * Right details panel is a separate layout region beside the graph — never
 * an overlay — sized ~25-30% of the workspace with sensible min/max widths.
 */
const DETAILS_PANEL_WIDTH = 'clamp(320px, 27vw, 440px)';
const HEADER_ALLOWANCE = 72;
const TOOLBAR_ALLOWANCE = 60;
const CLUSTER_BAR_ALLOWANCE = 46;

interface ViewState {
  visibleNodeIds: string[];
  expandedClusters: IntelClusterKey[];
  hiddenNodeIds: string[];
}

const EMPTY_SNAPSHOT: IntelligenceGraphSnapshot = { nodes: [], edges: [] };

export const DefenseTechIntelligenceTemplate: FC = () => {
  const builderRef = useRef<IntelligenceGraphBuilder>(
    new IntelligenceGraphBuilder(),
  );
  const [snapshot, setSnapshot] =
    useState<IntelligenceGraphSnapshot>(EMPTY_SNAPSHOT);
  const [rootId, setRootId] = useState<string | null>(null);
  const [visibleNodeIds, setVisibleNodeIds] = useState<Set<string>>(new Set());
  const [hiddenNodeIds, setHiddenNodeIds] = useState<Set<string>>(new Set());
  const [expandedClusters, setExpandedClusters] = useState<
    Set<IntelClusterKey>
  >(new Set());
  /** Node id → anchor node id it should first appear near. */
  const anchorsRef = useRef<Map<string, string>>(new Map());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<IntelEdge | null>(null);
  const [highlightedNodeIds, setHighlightedNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [searching, setSearching] = useState(false);
  const [expandingNodeId, setExpandingNodeId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [resultSize, setResultSize] = useState<ResultSize>(100);
  const [panelOpen, setPanelOpen] = useState(true);
  const [fitSignal, setFitSignal] = useState(0);
  const [history, setHistory] = useState<ViewState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [, windowHeight] = useWindowSize();

  /**
   * The force simulation/rendering bounds use ONLY the graph container's
   * measured dimensions (never the window), so nodes, labels and edges can
   * never render underneath the separate details panel region. Collapsing
   * the panel resizes the container, and the observer recalculates.
   */
  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const [graphSize, setGraphSize] = useState({ width: 960, height: 600 });
  useEffect(() => {
    const element = graphContainerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setGraphSize({
        width: Math.max(Math.floor(rect.width), 320),
        height: Math.max(Math.floor(rect.height), 320),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const graphWidth = graphSize.width;
  const graphHeight = Math.max(
    Math.min(
      graphSize.height,
      (windowHeight || 800) -
        HEADER_ALLOWANCE -
        TOOLBAR_ALLOWANCE -
        CLUSTER_BAR_ALLOWANCE,
    ),
    320,
  );

  const clusters: IntelCluster[] = useMemo(
    () => (rootId ? buildClusters(snapshot, rootId) : []),
    [snapshot, rootId],
  );

  const nodeById = useMemo(() => {
    const map = new Map<string, IntelNode>();
    snapshot.nodes.forEach((n) => map.set(n.id, n));
    return map;
  }, [snapshot]);

  const degreeById = useMemo(() => {
    const map = new Map<string, number>();
    snapshot.edges.forEach((e) => {
      map.set(e.source, (map.get(e.source) ?? 0) + 1);
      map.set(e.target, (map.get(e.target) ?? 0) + 1);
    });
    return map;
  }, [snapshot]);

  const pushHistory = useCallback(
    (
      visible: Set<string>,
      expanded: Set<IntelClusterKey>,
      hidden: Set<string>,
    ) => {
      setHistory((prev) => {
        const next = prev.slice(0, historyIndex + 1);
        next.push({
          visibleNodeIds: Array.from(visible),
          expandedClusters: Array.from(expanded),
          hiddenNodeIds: Array.from(hidden),
        });
        return next;
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex],
  );

  const applyViewState = (state: ViewState) => {
    setVisibleNodeIds(new Set(state.visibleNodeIds));
    setExpandedClusters(new Set(state.expandedClusters));
    setHiddenNodeIds(new Set(state.hiddenNodeIds));
  };

  const goBack = () => {
    if (historyIndex <= 0) return;
    applyViewState(history[historyIndex - 1]);
    setHistoryIndex(historyIndex - 1);
  };

  const goForward = () => {
    if (historyIndex >= history.length - 1) return;
    applyViewState(history[historyIndex + 1]);
    setHistoryIndex(historyIndex + 1);
  };

  /** Root search: runs BOTH APIs and rebuilds the graph around the entity. */
  const handleSearch = useCallback(
    async (query: string) => {
      setSearching(true);
      setErrors([]);
      setSelectedNodeId(null);
      setSelectedEdge(null);
      setHighlightedNodeIds(new Set());
      anchorsRef.current = new Map();

      const builder = new IntelligenceGraphBuilder();
      builderRef.current = builder;
      const root = builder.upsertNode({
        type: 'organization',
        label: query,
        dataset: 'publications',
        isRoot: true,
      });

      const result = await expandOrganization(builder, query, {
        size: resultSize,
        root: { rootId: root.id, rootName: query },
      });
      const nextSnapshot = builder.snapshot();
      logGraphIntegrityWarnings(validateGraphIntegrity(nextSnapshot, root.id));
      setSnapshot(nextSnapshot);
      setRootId(root.id);
      setErrors(result.errors);

      const visible = new Set([root.id]);
      const expanded = new Set<IntelClusterKey>();
      const hidden = new Set<string>();
      setVisibleNodeIds(visible);
      setExpandedClusters(expanded);
      setHiddenNodeIds(hidden);
      setHistory([
        {
          visibleNodeIds: [root.id],
          expandedClusters: [],
          hiddenNodeIds: [],
        },
      ]);
      setHistoryIndex(0);
      setSearching(false);
      setFitSignal((s) => s + 1);
    },
    [resultSize],
  );

  const toggleCluster = (key: IntelClusterKey) => {
    const cluster = clusters.find((c) => c.key === key);
    if (!cluster || !rootId) return;

    const nextExpanded = new Set(expandedClusters);
    const nextVisible = new Set(visibleNodeIds);
    if (nextExpanded.has(key)) {
      nextExpanded.delete(key);
      cluster.memberIds.forEach((id) => {
        if (id !== selectedNodeId) nextVisible.delete(id);
      });
    } else {
      nextExpanded.add(key);
      cluster.memberIds.slice(0, CLUSTER_EXPANSION_BATCH).forEach((id) => {
        nextVisible.add(id);
        if (!anchorsRef.current.has(id)) {
          anchorsRef.current.set(id, `cluster:${key}`);
        }
      });
    }
    setExpandedClusters(nextExpanded);
    setVisibleNodeIds(nextVisible);
    pushHistory(nextVisible, nextExpanded, hiddenNodeIds);
  };

  /** Expands an individual node by re-querying the appropriate API(s). */
  const handleNodeExpand = useCallback(
    async (nodeId: string) => {
      // Cluster virtual node → cluster toggle
      if (nodeId.startsWith('cluster:')) {
        toggleCluster(nodeId.replace('cluster:', '') as IntelClusterKey);
        return;
      }
      const builder = builderRef.current;
      const node = builder.graph.nodes.get(nodeId);
      if (!node || !isExpandableNode(node) || expandingNodeId) return;

      setExpandingNodeId(nodeId);
      setErrors([]);
      const rootNode = rootId ? builder.graph.nodes.get(rootId) : undefined;
      const result = await expandNodeService(builder, node, {
        size: resultSize,
        root:
          rootId && rootNode ? { rootId, rootName: rootNode.label } : undefined,
      });
      const nextSnapshot = builder.snapshot();
      logGraphIntegrityWarnings(validateGraphIntegrity(nextSnapshot, rootId));
      setSnapshot(nextSnapshot);
      setErrors(result.errors);

      // Reveal the expanded node's strongest new neighbors, anchored to it so
      // they animate outward without repositioning the rest of the graph.
      const neighborIds = new Set<string>();
      nextSnapshot.edges.forEach((e) => {
        if (e.source === nodeId) neighborIds.add(e.target);
        if (e.target === nodeId) neighborIds.add(e.source);
      });
      const newNeighbors = Array.from(neighborIds)
        .filter((id) => !visibleNodeIds.has(id))
        .sort((a, b) => {
          const na = builder.graph.nodes.get(a);
          const nb = builder.graph.nodes.get(b);
          return (nb?.recordCount ?? 0) - (na?.recordCount ?? 0);
        })
        .slice(0, NODE_EXPANSION_REVEAL);

      const nextVisible = new Set(visibleNodeIds);
      nextVisible.add(nodeId);
      newNeighbors.forEach((id) => {
        nextVisible.add(id);
        anchorsRef.current.set(id, nodeId);
      });
      // Nodes discovered in this expansion (even beyond direct neighbors)
      // stay accessible via clusters.
      setVisibleNodeIds(nextVisible);
      pushHistory(nextVisible, expandedClusters, hiddenNodeIds);
      setExpandingNodeId(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      expandingNodeId,
      resultSize,
      rootId,
      visibleNodeIds,
      expandedClusters,
      hiddenNodeIds,
      pushHistory,
      clusters,
    ],
  );

  const handleNodeSelect = (nodeId: string | null) => {
    setSelectedEdge(null);
    setHighlightedNodeIds(new Set());
    if (nodeId?.startsWith('cluster:')) {
      toggleCluster(nodeId.replace('cluster:', '') as IntelClusterKey);
      return;
    }
    setSelectedNodeId(nodeId);
    if (nodeId) setPanelOpen(true);
  };

  const handleLinkSelect = (link: RenderLink) => {
    const edge = snapshot.edges.find(
      (e) =>
        e.source === link.source &&
        e.target === link.target &&
        e.type === link.edgeType,
    );
    if (edge) {
      setSelectedEdge(edge);
      setSelectedNodeId(null);
      setPanelOpen(true);
    }
  };

  /**
   * First iteration of Find Connections: BFS shortest path from the node to
   * the root entity over the fused graph, highlighting the path nodes. The
   * evidence-preserving edge structure is already suited to weighted-path
   * scoring later.
   */
  const findConnections = (nodeId: string) => {
    if (!rootId || nodeId === rootId) return;
    const adjacency = new Map<string, string[]>();
    snapshot.edges.forEach((e) => {
      adjacency.set(e.source, [...(adjacency.get(e.source) ?? []), e.target]);
      adjacency.set(e.target, [...(adjacency.get(e.target) ?? []), e.source]);
    });
    const previous = new Map<string, string>();
    const queue = [nodeId];
    const seen = new Set([nodeId]);
    while (queue.length > 0) {
      const current = queue.shift() as string;
      if (current === rootId) break;
      (adjacency.get(current) ?? []).forEach((next) => {
        if (!seen.has(next)) {
          seen.add(next);
          previous.set(next, current);
          queue.push(next);
        }
      });
    }
    if (!previous.has(rootId)) return;
    const path: string[] = [];
    let cursor: string | undefined = rootId;
    while (cursor) {
      path.push(cursor);
      cursor = previous.get(cursor);
    }
    const nextVisible = new Set(visibleNodeIds);
    path.forEach((id, index) => {
      nextVisible.add(id);
      if (!anchorsRef.current.has(id) && index > 0) {
        anchorsRef.current.set(id, path[index - 1]);
      }
    });
    setVisibleNodeIds(nextVisible);
    setHighlightedNodeIds(new Set(path));
  };

  const contextActions: ContextMenuActions = {
    onExpand: (nodeId) => handleNodeExpand(nodeId),
    onFocus: (nodeId) => {
      setSelectedNodeId(nodeId);
      setHighlightedNodeIds(new Set([nodeId]));
      setPanelOpen(true);
    },
    onHide: (nodeId) => {
      const nextHidden = new Set(hiddenNodeIds);
      nextHidden.add(nodeId);
      setHiddenNodeIds(nextHidden);
      if (selectedNodeId === nodeId) setSelectedNodeId(null);
      pushHistory(visibleNodeIds, expandedClusters, nextHidden);
    },
    onFindConnections: findConnections,
    onViewEvidence: (nodeId) => {
      setSelectedNodeId(nodeId);
      setSelectedEdge(null);
      setPanelOpen(true);
    },
    onOpenSource: (nodeId) => {
      const node = nodeById.get(nodeId);
      const url = node?.evidence.find((e) => e.url)?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },
  };

  const resetGraph = () => {
    if (!rootId) return;
    const visible = new Set([rootId]);
    const expanded = new Set<IntelClusterKey>();
    const hidden = new Set<string>();
    setVisibleNodeIds(visible);
    setExpandedClusters(expanded);
    setHiddenNodeIds(hidden);
    setSelectedNodeId(null);
    setSelectedEdge(null);
    setHighlightedNodeIds(new Set());
    pushHistory(visible, expanded, hidden);
    setFitSignal((s) => s + 1);
  };

  // ---- Render model: visible entity nodes + collapsed cluster nodes ----
  const { renderNodes, renderLinks } = useMemo(() => {
    if (!rootId) return { renderNodes: [], renderLinks: [] };

    const nodes: RenderNode[] = [];
    const shown = new Set<string>();
    visibleNodeIds.forEach((id) => {
      if (hiddenNodeIds.has(id)) return;
      const node = nodeById.get(id);
      if (!node) return;
      shown.add(id);
      nodes.push({
        id: node.id,
        kind: 'entity',
        nodeType: node.type,
        label: node.label,
        recordCount: node.recordCount,
        connectionCount: degreeById.get(node.id) ?? 0,
        evidenceCount: node.evidence.length,
        datasets: node.datasets,
        isRoot: node.isRoot,
        anchorId: anchorsRef.current.get(node.id) ?? rootId,
      });
    });

    const links: RenderLink[] = [];
    const seenLinks = new Set<string>();
    snapshot.edges.forEach((edge) => {
      if (!shown.has(edge.source) || !shown.has(edge.target)) return;
      const key = `${edge.source}|${edge.type}|${edge.target}`;
      if (seenLinks.has(key)) return;
      seenLinks.add(key);
      links.push({
        source: edge.source,
        target: edge.target,
        edgeType: edge.type,
        relationshipType: edge.relationshipType,
        confidence: edge.confidence,
        evidenceCount: edge.provenance.length,
      });
    });

    // Collapsed clusters render as category nodes with counts around the root.
    clusters.forEach((cluster) => {
      const remaining = cluster.memberIds.filter(
        (id) => !shown.has(id) && !hiddenNodeIds.has(id),
      );
      if (expandedClusters.has(cluster.key) && remaining.length === 0) return;
      const clusterId = `cluster:${cluster.key}`;
      nodes.push({
        id: clusterId,
        kind: 'cluster',
        nodeType: cluster.nodeType,
        label: cluster.label,
        count: remaining.length,
        recordCount: remaining.length,
        connectionCount: 1,
        evidenceCount: 0,
        datasets: [],
        anchorId: rootId,
      });
      links.push({
        source: rootId,
        target: clusterId,
        edgeType: 'CLUSTER',
        relationshipType: 'derived',
        confidence: 1,
        evidenceCount: 0,
      });
    });

    return { renderNodes: nodes, renderLinks: links };
  }, [
    rootId,
    visibleNodeIds,
    hiddenNodeIds,
    nodeById,
    degreeById,
    snapshot,
    clusters,
    expandedClusters,
  ]);

  const selectedNode = selectedNodeId ? nodeById.get(selectedNodeId) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${HEADER_ALLOWANCE}px)`,
        overflow: 'hidden',
      }}
    >
      <IntelligenceGraphToolbar
        onSearch={handleSearch}
        searching={searching}
        resultSize={resultSize}
        onResultSizeChange={setResultSize}
        graphNodeLabels={renderNodes
          .filter((n) => n.kind === 'entity')
          .map((n) => ({ id: n.id, label: n.label }))}
        onLocateNode={(nodeId) => {
          setSelectedNodeId(nodeId);
          setHighlightedNodeIds(new Set([nodeId]));
          setPanelOpen(true);
        }}
        canGoBack={historyIndex > 0}
        canGoForward={historyIndex < history.length - 1}
        onBack={goBack}
        onForward={goForward}
        onFit={() => setFitSignal((s) => s + 1)}
        onReset={resetGraph}
        initialQuery={DEFAULT_SEARCH_ENTITY}
      />
      {(searching || expandingNodeId) && <LinearProgress />}
      {errors.map((error) => (
        <Alert
          key={error}
          severity="warning"
          onClose={() => setErrors((prev) => prev.filter((e) => e !== error))}
        >
          {error}
        </Alert>
      ))}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          ref={graphContainerRef}
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 320,
            overflow: 'hidden',
          }}
        >
          {rootId ? (
            <>
              <IntelligenceGraph
                nodes={renderNodes}
                links={renderLinks}
                width={graphWidth}
                height={graphHeight}
                selectedNodeId={selectedNodeId}
                highlightedNodeIds={highlightedNodeIds}
                onNodeSelect={handleNodeSelect}
                onNodeExpand={handleNodeExpand}
                onLinkSelect={handleLinkSelect}
                contextActions={contextActions}
                fitSignal={fitSignal}
              />
              <Box sx={{ position: 'absolute', left: 12, top: 12 }}>
                <GraphLegend />
              </Box>
            </>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ height: '100%', px: 4, textAlign: 'center' }}
            >
              <Icon icon="mdi:radar" width={56} color="#dc2626" />
              <Typography variant="h5" fontWeight={700}>
                Defense Technology Intelligence Graph
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
                Search an organization, person, technology or PLA unit to fuse
                Publications research intelligence with PLA procurement
                intelligence into one evidence-backed graph. Try the default
                entity {DEFAULT_SEARCH_ENTITY} (CETC 14th Research Institute).
              </Typography>
              {searching && (
                <Typography variant="caption" color="text.secondary">
                  Querying Publications and PLA Contracts APIs…
                </Typography>
              )}
            </Stack>
          )}
          <Tooltip title={panelOpen ? 'Collapse panel' : 'Open panel'}>
            <IconButton
              size="small"
              onClick={() => setPanelOpen((open) => !open)}
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: 2,
              }}
            >
              <Icon
                icon={panelOpen ? 'mdi:chevron-right' : 'mdi:chevron-left'}
              />
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={panelOpen} orientation="horizontal">
          <Paper
            square
            variant="outlined"
            sx={{
              width: DETAILS_PANEL_WIDTH,
              minWidth: 320,
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              borderTop: 0,
              borderBottom: 0,
            }}
          >
            <EntityDetailsPanel
              node={selectedNode}
              edge={selectedEdge}
              snapshot={snapshot}
              expandable={Boolean(
                selectedNode && isExpandableNode(selectedNode),
              )}
              expanding={Boolean(
                selectedNode && expandingNodeId === selectedNode.id,
              )}
              onExpand={(node) => handleNodeExpand(node.id)}
              onSelectNode={(nodeId) => handleNodeSelect(nodeId)}
              onHighlightNodes={(nodeIds) => {
                setHighlightedNodeIds(new Set(nodeIds));
                setVisibleNodeIds((prev) => {
                  const next = new Set(prev);
                  nodeIds.forEach((id) => next.add(id));
                  return next;
                });
              }}
              onClose={() => {
                setSelectedNodeId(null);
                setSelectedEdge(null);
              }}
            />
          </Paper>
        </Collapse>
      </Box>

      <Divider />
      <GraphClusterBar
        clusters={clusters}
        expandedClusters={expandedClusters}
        onToggleCluster={toggleCluster}
      />
    </Box>
  );
};

// Re-exported for tests: id used for the default entity's root node.
export const DEFAULT_ROOT_NODE_ID = makeNodeId(
  'organization',
  DEFAULT_SEARCH_ENTITY,
);
